import {
  Response,
  Account,
  BaseTransaction,
  apiAbortController,
  TransactionWarning,
} from '@ui/src';
import api from '../../../../api/api';
import {
  CustomerProps,
  CustomerScore,
  CustomerScoreQueuedProps,
  Balance,
  CustomerRanges,
  OngoingMonitring,
} from '../../../customers/types';
import {
  BaseInfoFormValues,
  Device,
  UserAccountProps,
  Lock,
  LockFormValues,
  LockAuditLog,
  DeviceSession,
} from '../../types';

export const individualCustomersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIndividualUsers: build.query<Response<CustomerProps>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualUser: build.query<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualUserScore: build.query<{ data: CustomerScore }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/current-score`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualAmlUpdatingItem: build.query<
      { data: CustomerScoreQueuedProps },
      { id: string; orgId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/clients-scores-queue-items/${data.id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualLastCalculatingScore: build.query<
      Response<CustomerScore>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/scores`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualOngoingMonitoring: build.query<
      Response<OngoingMonitring>,
      { id: string; cursor?: string; take?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/warnings-groups?take=${
          data.take || '10'
        }${data.cursor ? `&cursor=${data.cursor}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualOngoingMonitoringItem: build.query<
      { data: OngoingMonitring },
      { orgId: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyIndividualOngoingWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
        body: {
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchApplyIndividualOngoingWarningAction: build.mutation<
      { data: any },
      {
        orgId: string;
        body: {
          warningGroupId: string;
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/warnings/batch-action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualAccountBalance: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockIndividualCredit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/block-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockIndividualCredit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/unblock-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockIndividualDebit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/block-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockIndividualDebit: build.mutation<
      { data: Account },
      { id: string; accountId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/unblock-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    closeIndividualAccount: build.mutation<
      { data: Account },
      { id: string; accountId: string; body: { comment: string } }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/close`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    openIndividualAccount: build.mutation<
      { data: Account },
      { id: string; accountId: string; body: { comment: string } }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/accounts/${data.accountId}/open`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualUserScore: build.mutation<
      {
        data: CustomerScoreQueuedProps;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/clients-scores-queue-items`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualUserBaseInformation: build.mutation<
      {
        data: CustomerProps;
      },
      { body: BaseInfoFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    createIndividualAccount: build.mutation<
      {
        data: UserAccountProps;
      },
      {
        organizationId: string;
        body: { description: string };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.organizationId}/account`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualRanges: build.mutation<
      { data: CustomerProps },
      { id: string; body: CustomerRanges }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/ranges`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualVisibility: build.mutation<
      { data: Account & { visibleToClient: boolean } },
      { id: string; body: { visibleToClient: boolean; id: string } }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualTransactionsWithWarnings: build.query<
      Response<BaseTransaction>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/individuals/${id}/transactions-with-warnings${query.replace(
          'take=20',
          'take=10',
        )}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportIndividualStatements: build.query<
      string,
      { id: string; format: string; account: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/statements-items?format=${data.format}&account=${data.account}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    convertCofToIndividual: build.mutation<{ data: CustomerProps }, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/convert-to-individual`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMainChannel: build.query<Response<{ id: string }>, string>({
      query: (id) => ({
        url: `/v1/channels?organizationId=${id}&channelType=MAIN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividaulAccountLogs: build.query<{ data: UserAccountProps }, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetIndividualWarningGroupAction: build.mutation<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualWarningGroupWithPagination: build.query<
      Response<TransactionWarning>,
      { id: string; warningGroupId: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/warnings-groups/${data.warningGroupId}/warnings?cursor=${data.cursor}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDevices: build.query<
      Response<Device>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/${id}/devices${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerActiveLocks: build.query<Response<Lock>, string>({
      query: (id) => ({
        url: `/v1/locks?take=1&locked=true&userId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomerLock: build.mutation<{ data: Lock }, LockFormValues>({
      query: (body) => ({
        url: '/v1/locks',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualManualRiskScore: build.mutation<
      { data: CustomerScore },
      { id: string; totalRiskScore: number }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/scores`,
        method: 'POST',
        body: {
          totalRiskScore: data.totalRiskScore,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocksAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLockItemAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerLocksAuditLogItem: build.query<{ data: LockAuditLog }, string>({
      query: (id) => ({
        url: `/v1/idm-audit-log/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    endCustomerSession: build.mutation<
      { data: { count: number } },
      {
        id: string;
        deviceId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/devices/${data.deviceId}/sessions`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDeviceSessions: build.query<
      Response<DeviceSession>,
      { id: string; entityId: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/device/${data.entityId}/sessions${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualCustomerClosingBalances: build.query<
      { data: Account },
      { id: string; closingBalancesAt?: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}${
          data.closingBalancesAt
            ? `?closingBalancesAt=${data.closingBalancesAt}`
            : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualOngoingWarning: build.query<
      { data: TransactionWarning },
      {
        orgId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.orgId}/warnings-groups/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualAccountClosingBalances: build.query<
      { data: Balance[] },
      string
    >({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetIndividualUsersQuery,
  useLazyGetIndividualUsersQuery,
  useLazyGetIndividualUserQuery,
  useLazyGetIndividualUserScoreQuery,
  useCreateIndividualUserScoreMutation,
  useUpdateIndividualUserBaseInformationMutation,
  useLazyGetIndividualAmlUpdatingItemQuery,
  useLazyGetIndividualLastCalculatingScoreQuery,
  useLazyGetIndividualAccountBalanceQuery,
  useBlockIndividualCreditMutation,
  useBlockIndividualDebitMutation,
  useUnblockIndividualCreditMutation,
  useUnblockIndividualDebitMutation,
  useCreateIndividualAccountMutation,
  useUpdateIndividualRangesMutation,
  useLazyGetIndividualTransactionsWithWarningsQuery,
  useCloseIndividualAccountMutation,
  useOpenIndividualAccountMutation,
  useLazyGetIndividualOngoingMonitoringQuery,
  useLazyExportIndividualStatementsQuery,
  useLazyGetIndividualOngoingMonitoringItemQuery,
  useApplyIndividualOngoingWarningActionMutation,
  useBatchApplyIndividualOngoingWarningActionMutation,
  useConvertCofToIndividualMutation,
  useLazyGetMainChannelQuery,
  useUpdateIndividualVisibilityMutation,
  useLazyGetIndividaulAccountLogsQuery,
  useResetIndividualWarningGroupActionMutation,
  useLazyGetIndividualWarningGroupWithPaginationQuery,
  useLazyGetCustomerDevicesQuery,
  useLazyGetCustomerLockItemAuditLogsQuery,
  useLazyGetCustomerLockItemQuery,
  useLazyGetCustomerLocksAuditLogItemQuery,
  useLazyGetCustomerLocksAuditLogsQuery,
  useLazyGetCustomerLocksQuery,
  useCreateCustomerLockMutation,
  useUpdateCustomerLockMutation,
  useEndCustomerSessionMutation,
  useLazyGetCustomerActiveLocksQuery,
  useLazyGetCustomerDeviceSessionsQuery,
  useLazyGetIndividualCustomerClosingBalancesQuery,
  useLazyGetIndividualOngoingWarningQuery,
  useCreateIndividualManualRiskScoreMutation,
  useLazyGetIndividualAccountClosingBalancesQuery,
} = individualCustomersApi;

export const {
  endpoints: {
    getIndividualUsers,
    getIndividualUser,
    updateIndividualUserBaseInformation,
    getIndividualUserScore,
    createIndividualUserScore,
    getIndividualAmlUpdatingItem,
    getIndividualLastCalculatingScore,
    getIndividualAccountBalance,
    blockIndividualCredit,
    blockIndividualDebit,
    unblockIndividualCredit,
    unblockIndividualDebit,
    updateIndividualRanges,
    getIndividualTransactionsWithWarnings,
    closeIndividualAccount,
    openIndividualAccount,
    exportIndividualStatements,
    getIndividualOngoingMonitoring,
    getIndividualOngoingMonitoringItem,
    convertCofToIndividual,
    getMainChannel,
    updateIndividualVisibility,
    getIndividaulAccountLogs,
    resetIndividualWarningGroupAction,
    getIndividualWarningGroupWithPagination,
    getCustomerDevices,
    getCustomerLockItem,
    getCustomerLockItemAuditLogs,
    getCustomerLocksAuditLogItem,
    getCustomerLocks,
    getCustomerLocksAuditLogs,
    createCustomerLock,
    updateCustomerLock,
    endCustomerSession,
    getCustomerActiveLocks,
    getCustomerDeviceSessions,
    getIndividualCustomerClosingBalances,
    getIndividualOngoingWarning,
    createIndividualManualRiskScore,
    getIndividualAccountClosingBalances,
  },
} = individualCustomersApi;
